import Bresenham from '@FreeRider/Utils/Bresenham';

import Sector from '@FreeRider/Track/Sector';
import Mass from '@FreeRider/Physics/Mass';

/*---------------- LINES ----------------------*/
import PhysicsLine from '@FreeRider/Track/Lines/PhysicsLine';
import SceneryLine from '@FreeRider/Track/Lines/SceneryLine';
import Line from '@FreeRider/Track/Lines/Line';

/*---------------- POWERUPS ----------------------*/
import Powerup from '@FreeRider/Track/Powerups/Powerup';
import Boost from '@FreeRider/Track/Powerups/Boost';
import Slowmotion from '@FreeRider/Track/Powerups/Slowmotion';
import Bomb from '@FreeRider/Track/Powerups/Bomb';
import Gravity from '@FreeRider/Track/Powerups/Gravity';
import Antigravity from '@FreeRider/Track/Powerups/Antigravity';
import Star from '@FreeRider/Track/Powerups/Star';
import Checkpoint from '@FreeRider/Track/Powerups/Checkpoint';

class Track
{
    public static readonly SECTOR_SIZE = 100;

    public sectors: Sector[];

    public sectorsMap: Sector[][];

    /**
     * Constructor
     */
    constructor()
    {
        this.sectors = [];
        this.sectorsMap = [];
    }

    /**
     * Loads the track
     * @param trackData
     */
    public async load(trackData: string): Promise<void>
    {
        const trackSections: string[] = trackData.split('#');

        let physicsLineData: string[] = [];
        let sceneryLineData: string[] = [];
        let powerupsData: string[] = [];

        if (trackSections.length > 0)
        {
            physicsLineData = trackSections[0].split(',');
        }

        if (trackSections.length > 1)
        {
            sceneryLineData = trackSections[1].split(',');
        }

        if (trackSections.length > 2)
        {
            powerupsData = trackSections[2].split(',');
        }

        this.addLines(physicsLineData, PhysicsLine);
        this.addLines(sceneryLineData, SceneryLine);
        this.addPowerups(powerupsData);
    }

    /**
     * Adds lines to the track
     * @param lines
     * @param callback
     */
    private addLines(lines: string[], lineConstructor: Constructor<Line>)
    {
        const lineCount = lines.length;
        for (let i = 0; i < lineCount; i++)
        {
            var coords = lines[i].split(' ');
            var coordsCount = coords.length;
            if (coordsCount > 3)
            {
                var coordsParsed = [];
                for (var k = 0; k < coordsCount - 2; k += 2)
                {
                    var p1x = parseInt(coords[k], 32);
                    var p1y = parseInt(coords[k + 1], 32);
                    var p2x = parseInt(coords[k + 2], 32);
                    var p2y = parseInt(coords[k + 3], 32);

                    this.addLine(p1x, p1y, p2x, p2y, lineConstructor);
                }
            }
        }
    }

    /**
     * Adds a line to the track
     * @param p1x
     * @param p1y
     * @param p2x
     * @param p2y
     * @param lineConstructor
     */
    private addLine(p1x: number, p1y: number, p2x: number, p2y: number, lineConstructor: Constructor<Line>)
    {
        p1x = Math.round(p1x);
        p1y = Math.round(p1y);
        p2x = Math.round(p2x);
        p2y = Math.round(p2y);

        const ppx = p2x - p1x;
        const ppy = p2y - p1y;

        const length = Math.sqrt(Math.pow(ppx, 2) + Math.pow(ppy, 2));

        let line: Line | false = false;

        if (length >= 2)
        {
            line = new lineConstructor(p1x, p1y, p2x, p2y);

            const sectoredCoordinates = Bresenham.parse(p1x, p1y, p2x, p2y, Track.SECTOR_SIZE);
            const sectoredCoordinatesCount = sectoredCoordinates.length;

            for (let i = 0; i < sectoredCoordinatesCount; i += 2)
            {
                const sectoredLineCoordinateX = sectoredCoordinates[i];
                const sectoredLineCoordinateY = sectoredCoordinates[i + 1];

                this.addObject(sectoredLineCoordinateX, sectoredLineCoordinateY, line);
            }
        }

        return line;
    }

    /**
     * Adds powerups to the track
     * @param powerups
     */
    private addPowerups(powerupsData: string[])
    {
        const powerupsCount = powerupsData.length;

        for (let i = 0; i < powerupsCount; i++)
        {
            const powerupCoordinates = powerupsData[i].split(' ');
            const powerupCoordinatesCount = powerupCoordinates.length;

            if (powerupCoordinatesCount >= 2)
            {
                const paramsDecoded = [];

                for (let k = 1; k < powerupCoordinatesCount; k++)
                {
                    paramsDecoded.push(parseInt(powerupCoordinates[k], 32));
                }

                const pX = Math.round(paramsDecoded[0]);
                const pY = Math.round(paramsDecoded[1]);

                let powerupInstance: Powerup;

                const type = powerupCoordinates[0];

                switch (type)
                {
                    case 'B':
                        // powerupInstance = new Boost(pX, pY, paramsDecoded[2]);
                        break;
                    case 'S':
                        // powerupInstance = new Slowmotion(pX, pY);
                        break;
                    case 'O':
                        // powerupInstance = new Bomb(pX, pY);
                        break;
                    case 'G':
                        // powerupInstance = new Gravity(pX, pY, paramsDecoded[2]);
                        break;
                    case 'C':
                        powerupInstance = new Checkpoint(pX, pY);
                        break;
                    case 'T':
                        powerupInstance = new Star(pX, pY);
                        break;
                    case 'A':
                        // powerupInstance = new Antigravity(pX, pY);
                        break;
                }

                if (powerupInstance instanceof Powerup)
                {
                    this.addObject(pX, pY, powerupInstance);
                }
            }
        }
    }

    /**
     * Add Object
     * @param p1x
     * @param p1y
     * @param object
     */
    private addObject(pX: number, pY: number, object: Line | Powerup)
    {
        const sectorX = Math.floor(pX / Track.SECTOR_SIZE);
        const sectorY = Math.floor(pY / Track.SECTOR_SIZE);

        const sectors = this.sectorsMap;

        let newSector: Sector | false;

        if (typeof sectors[sectorX] === 'undefined')
        {
            sectors[sectorX] = [];
        }

        if (typeof sectors[sectorX][sectorY] === 'undefined')
        {
            newSector = sectors[sectorX][sectorY] = new Sector(sectorX, sectorY);
        }

        const sector = sectors[sectorX][sectorY];

        if (object instanceof Line)
        {
            sector.addLine(object);
        }
        else
        {
            sector.addPowerup(object);
        }

        if (newSector)
        {
            this.sectors.push(newSector);
        }
    }

    /**
     * Adds a physics line to the track
     * @param p1x
     * @param p1y
     * @param p2x
     * @param p2y
     */
    private addPhysicsLine(p1x: number, p1y: number, p2x: number, p2y: number)
    {
        return this.addLine(p1x, p1y, p2x, p2y, PhysicsLine);
    }

    /**
     * Adds a scenery line to the track
     * @param p1x
     * @param p1y
     * @param p2x
     * @param p2y
     */
    private addSceneryLine(p1x: number, p1y: number, p2x: number, p2y: number)
    {
        return this.addLine(p1x, p1y, p2x, p2y, SceneryLine);
    }

    /**
     * Collide
     * @param mass
     */
    public collide(mass: Mass)
    {
        const position = mass.position;

        const sectorSize = 100;
        const x = Math.floor((position.x) / sectorSize - 0.5);
        const y = Math.floor((position.y) / sectorSize - 0.5);

        const sectorMap = this.sectorsMap;

        if (sectorMap[x])
        {
            if (sectorMap[x][y])
            {
                sectorMap[x][y].resetCollided();
            }
        }

        if (sectorMap[x + 1])
        {
            if (sectorMap[x + 1][y])
            {
                sectorMap[x + 1][y].resetCollided();
            }
        }

        if (sectorMap[x + 1])
        {
            if (sectorMap[x + 1][y + 1])
            {
                sectorMap[x + 1][y + 1].resetCollided();
            }
        }

        if (sectorMap[x])
        {
            if (sectorMap[x][y + 1])
            {
                sectorMap[x][y + 1].resetCollided();
            }
        }

        if (sectorMap[x])
        {
            if (sectorMap[x][y])
            {
                sectorMap[x][y].collide(mass);
            }
        }

        if (sectorMap[x + 1])
        {
            if (sectorMap[x + 1][y])
            {
                sectorMap[x + 1][y].collide(mass);
            }
        }

        if (sectorMap[x + 1])
        {
            if (sectorMap[x + 1][y + 1])
            {
                sectorMap[x + 1][y + 1].collide(mass);
            }
        }
        if (sectorMap[x])
        {
            if (sectorMap[x][y + 1])
            {
                sectorMap[x][y + 1].collide(mass);
            }
        }
    }
}

export default Track;